import { Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Award } from '@/module/Author/Award';
import { AwardCard } from '@/module/Author/Award/components/Card/AwardCard';
import { CriteriaAwardSelectAddModal } from '@/module/Laboratory/criteria/components/CriteriaAwardSelectAddModal';
import { getCenterReportCriteriaDataOptions } from '@/module/ResearchCenter';

import { NoContentPlaceholder, PendingSpinner } from '@/core/components';

import { Id } from '@feature/utils';

import { CriteriaHeaderButtons } from './components/components/CriteriaHeaderButtons';
import { CriteriaItemDeleteButton } from './components/components/CriteriaItemDeleteButton';
import { useSaveCriteriaData } from './components/hooks/useSaveCriteriaData';

export interface CenterReportAwardSelectProps {
  labId: Id;
  criteriaCode: string;
}

export function CenterReportAwardSelect({ labId, criteriaCode }: CenterReportAwardSelectProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();
  const {
    data: {
      completed,
      awards: { available, selected },
    },
  } = useSuspenseQuery(getCenterReportCriteriaDataOptions(labId, criteriaCode));
  const { onSubmit, isPending } = useSaveCriteriaData(labId, criteriaCode, close);

  const onAdd = (award: Award) => onSubmit({ toAdd: award });
  const onRemove = (award: Award) => onSubmit({ toRemove: award });

  return (
    <div>
      <CriteriaHeaderButtons
        completed={completed}
        criteriaCode={criteriaCode}
        labId={labId}
        onAdd={open}
      />
      {opened && (
        <CriteriaAwardSelectAddModal
          available={available}
          selected={selected}
          close={close}
          onAdd={onAdd}
        />
      )}
      <>{isPending && <PendingSpinner />}</>
      {!isPending && (
        <>
          {selected.length > 0 && (
            <Stack>
              {selected.map((award, index) => (
                <Flex
                  align="center"
                  key={award.id}
                >
                  {!completed && (
                    <CriteriaItemDeleteButton
                      index={index}
                      onRemove={() => onRemove(award)}
                    />
                  )}
                  <AwardCard
                    award={award}
                    onClick={() => window.open(`/author/award/${award.id}`)}
                  />
                </Flex>
              ))}
            </Stack>
          )}
          {selected.length === 0 && <NoContentPlaceholder title={t('centerReport.noCriteriaContent')} />}
        </>
      )}
    </div>
  );
}
